import type { Object3D } from 'three';

/**
 * Where a diff-added object belongs in the LIVE scene.
 *
 * `diff.added` is FLAT. `collectObjectsWithIds` walks the new composition
 * recursively, so an appliance nested inside an island group becomes an entry in
 * its own right alongside the group itself. Attaching those entries straight to
 * `objectHolder` reparents them: three.js `add()` detaches from the previous
 * parent and keeps the LOCAL transform, so the appliance stops being part of its
 * island.
 *
 * That is what put two sinks on screen. The appliance was attached loose to the
 * holder, `rebuildIsland` later swapped the island group — which no longer
 * contained it, so the swap could not take it away — and the fresh group arrived
 * carrying its own mesh for the same PanelItem id. The copies sit at the two
 * layouts they were built at: a populate that adds a fridge after the sink
 * redistributes the front panel from three groups to four, moving the sink from
 * x -11 to x 3.875, so the pair renders 14.875" apart at the same height and
 * depth. It is permanent because `snapshotScene` is a Map keyed by
 * `userData.id`: two objects collapse to one entry, and the diff manager can
 * never see or remove the extra one again. Only a full recompose clears it.
 *
 * The composition still holds the answer here — the object has not been detached
 * yet, so its own ancestors name the island it was built into. Falls back to the
 * holder for anything that isn't island-owned (floor, camera rigs, debug groups).
 */
export const resolveAddedParent = (objectHolder: Object3D, object: Object3D): Object3D => {
	for (let ancestor = object.parent; ancestor; ancestor = ancestor.parent) {
		if (ancestor.userData?.type === 'island') {
			const islandId = ancestor.userData?.id as string | null;
			if (!islandId) break;

			// Parents precede their children in `diff.added` (collectObjectsWithIds
			// records a node before recursing), so an island added in this same diff
			// is already attached by the time its appliances are applied.
			const liveIsland = objectHolder.children.find((child) => child.userData?.type === 'island' && child.userData?.id === islandId);
			return liveIsland ?? objectHolder;
		}
	}

	return objectHolder;
};
